import React, { useState, useEffect, useMemo } from "react";
import {
  getPartnerNotifications,
  markPartnerNotificationSeen,
  markAllPartnerNotificationsSeen,
} from "../../../../Service/partnerAPI";
import moment from "moment";
import {
  MdCalendarMonth,
  MdCheckCircle,
  MdClose,
  MdWork,
  MdOutlineNotifications,
  MdWarningAmber,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";

const FILTER_OPTIONS = [
  "All",
  "Today",
  "Last 2 Days",
  "Last Week",
  "Last Month",
  "Custom",
];

const PartnerViewAllNotificationsModal = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showConfirmMarkAll, setShowConfirmMarkAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await getPartnerNotifications();
      if (res && res.data) {
        setNotifications(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  console.log(notifications);

  const handleMarkAllAsReadConfirm = async () => {
    try {
      const res = await markAllPartnerNotificationsSeen();
      if (res && !res.error) {
        setNotifications((prev) =>
          prev.map((item) => ({ ...item, status: "seen" })),
        );
      }
      setShowConfirmMarkAll(false);
    } catch (error) {
      console.log(error);
      setShowConfirmMarkAll(false);
    }
  };

  const handleMarkAllAsReadClick = () => {
    setShowConfirmMarkAll(true);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?._id) return;

    const isUnseen =
      String(notification?.status || "").toLowerCase() === "unseen" ||
      String(notification?.status || "").toLowerCase() === "unread";

    if (isUnseen) {
      await markPartnerNotificationSeen(notification._id);
      setNotifications((prev) =>
        prev.map((item) =>
          item?._id === notification._id ? { ...item, status: "seen" } : item,
        ),
      );
    }

    const type = String(notification?.type || "").trim();
    const title = String(notification?.title || "").toLowerCase();

    if (type === "Visit") {
      onClose();
      if (
        title.includes("duty ended") ||
        title.includes("duty started") ||
        title.includes("standby specialist assigned")
      ) {
        navigate("/partner/visits");
      } else {
        navigate("/partner/bookings");
      }
    } else if (title.includes("support ticket") || type === "Support") {
      onClose();
      navigate("/partner/helpAdmin");
    }
  };

  const filteredNotifications = useMemo(() => {
    return notifications
      .filter((notif) => {
        const notifDate = moment(notif.createdAt);
        const today = moment().startOf("day");

        if (filter === "Today") {
          return notifDate.isSameOrAfter(today);
        }
        if (filter === "Last 2 Days") {
          const last2 = moment().subtract(2, "days").startOf("day");
          return notifDate.isSameOrAfter(last2);
        }
        if (filter === "Last 1 Week") {
          const lastWeek = moment().subtract(1, "weeks").startOf("day");
          return notifDate.isSameOrAfter(lastWeek);
        }
        if (filter === "This Month") {
          const thisMonth = moment().startOf("month");
          return notifDate.isSameOrAfter(thisMonth);
        }
        if (filter === "Custom") {
          if (startDate && endDate) {
            const start = moment(startDate).startOf("day");
            const end = moment(endDate).endOf("day");
            return notifDate.isBetween(start, end, null, "[]");
          } else if (startDate) {
            const start = moment(startDate).startOf("day");
            return notifDate.isSameOrAfter(start);
          } else if (endDate) {
            const end = moment(endDate).endOf("day");
            return notifDate.isSameOrBefore(end);
          }
          return true;
        }
        // "All"
        return true;
      })
      .sort(
        (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf(),
      );
  }, [notifications, filter, startDate, endDate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 md:p-6">
      <div className="bg-[#F6F8FC] rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl overflow-hidden relative">
        {/* Modal Header */}
        <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center z-10 shrink-0">
          <h2 className="text-[18px] font-bold text-gray-800 font-poppins">
            Notification Center
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-800"
          >
            <MdClose size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <div className="max-w-4xl mx-auto">
            {/* Header & Filters */}
            <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <h1 className="text-[16px] font-bold text-gray-800 font-poppins">
                    All Notifications
                  </h1>
                  <span className="bg-blue-100 text-blue-700 py-0.5 px-2.5 rounded-full text-[12px] font-semibold">
                    {filteredNotifications.length}
                  </span>
                </div>

                {(() => {
                  const hasUnread = notifications.some(
                    (notif) =>
                      String(notif?.status || "").toLowerCase() === "unseen" ||
                      String(notif?.status || "").toLowerCase() === "unread"
                  );
                  return (
                    <div className="flex flex-wrap items-center gap-3">
                      {filter === "Custom" && (
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            max={moment().format("YYYY-MM-DD")}
                            value={startDate}
                            onChange={(e) => {
                              const val = e.target.value;
                              const today = moment().format("YYYY-MM-DD");
                              if (val > today) {
                                setStartDate("");
                              } else {
                                setStartDate(val);
                                if (endDate && val > endDate) {
                                  setEndDate("");
                                }
                              }
                            }}
                            className="bg-[#F6F8FC] rounded-lg px-3 py-2 text-[13px] font-poppins border border-gray-200 outline-none focus:border-blue-500"
                          />
                          <span className="text-gray-400 font-poppins text-[12px]">
                            to
                          </span>
                          <input
                            type="date"
                            min={startDate}
                            max={moment().format("YYYY-MM-DD")}
                            value={endDate}
                            onChange={(e) => {
                              const val = e.target.value;
                              const today = moment().format("YYYY-MM-DD");
                              if (val > today || (startDate && val < startDate)) {
                                setEndDate("");
                              } else {
                                setEndDate(val);
                              }
                            }}
                            className="bg-[#F6F8FC] rounded-lg px-3 py-2 text-[13px] font-poppins border border-gray-200 outline-none focus:border-blue-500"
                          />
                        </div>
                      )}

                      <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="bg-[#F6F8FC] rounded-lg px-3 py-2 text-[13px] font-poppins outline-none focus:border-blue-500 border border-gray-200 cursor-pointer min-w-[140px]"
                      >
                        {FILTER_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>

                      <button
                        onClick={handleMarkAllAsReadClick}
                        disabled={!hasUnread}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-medium font-poppins transition-colors ${
                          hasUnread
                            ? "bg-blue-50 text-blue-600 hover:bg-blue-100"
                            : "bg-gray-50 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        <MdCheckCircle size={16} />
                        Mark all as read
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Notification List */}
            {loading ? (
              <div className="flex flex-col items-center justify-center h-64">
                <span className="inline-block w-8 h-8 border-2 border-[#3E57E8] border-t-transparent rounded-full animate-spin mb-4"></span>
                <p className="font-poppins text-[14px] text-gray-500">
                  Loading notifications...
                </p>
              </div>
            ) : filteredNotifications.length > 0 ? (
              <div className="space-y-4 pb-8">
                {filteredNotifications.map((notif) => {
                  const isUnseen =
                    String(notif?.status || "").toLowerCase() === "unseen" ||
                    String(notif?.status || "").toLowerCase() === "unread";
                  const isVisit = String(notif?.type || "").trim() === "Visit";

                  return (
                    <div
                      key={notif._id || notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`p-5 rounded-xl border transition-all duration-300 ${
                        isUnseen
                          ? "bg-white border-[#3E57E8] shadow-md border-l-4"
                          : "bg-white border-gray-200 hover:border-gray-300 shadow-sm"
                      } ${isVisit ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg" : "cursor-default"}`}
                    >
                      <div className="flex gap-4">
                        <div
                          className={`mt-1 h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isUnseen
                              ? "bg-blue-100 text-blue-600"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {(() => {
                            const type = String(notif?.type || "").trim();
                            const title = String(
                              notif?.title || "",
                            ).toLowerCase();

                            if (type === "Visit") {
                              if (
                                title.includes("duty ended") ||
                                title.includes("duty started") ||
                                title.includes("standby specialist assigned")
                              ) {
                                return <MdWork size={20} />;
                              }
                              return <MdCalendarMonth size={20} />;
                            }
                            return (
                              <div className="h-2.5 w-2.5 rounded-full bg-current"></div>
                            );
                          })()}
                        </div>

                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-1 md:gap-4 mb-2">
                            <h3
                              className={`text-[15px] font-semibold font-poppins ${
                                isUnseen ? "text-[#1D1D22]" : "text-gray-700"
                              }`}
                            >
                              {notif.title}
                            </h3>
                            <span className="text-[12px] text-gray-500 font-poppins font-medium whitespace-nowrap">
                              {moment(notif.createdAt).format(
                                "MMM DD, YYYY • hh:mm A",
                              )}
                            </span>
                          </div>
                          <p className="text-[14px] text-gray-600 font-poppins leading-relaxed">
                            {notif.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MdOutlineNotifications size={36} className="text-gray-300" />
                </div>
                <h3 className="text-[16px] font-semibold text-gray-800 font-poppins mb-1">
                  No notifications found
                </h3>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmMarkAll && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4">
                <MdWarningAmber size={28} />
              </div>
              <h3 className="text-[16px] font-bold text-gray-800 font-poppins mb-2">
                Mark All as Read
              </h3>
              <p className="text-[13px] text-gray-600 font-poppins mb-6">
                Are you sure you want to mark all your notifications as read?
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowConfirmMarkAll(false)}
                  className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium font-poppins text-[13px] hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkAllAsReadConfirm}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white font-medium font-poppins text-[13px] hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerViewAllNotificationsModal;
